import { useEffect, useState } from "react";

/**
 * Cena decorativa do painel esquerdo do login: pátio noturno com três
 * ônibus elétricos, dois carregadores e cabos com energia correndo.
 *
 * Três coisas importam mais que o desenho:
 *
 *  1. **É decoração e nada mais.** O bloco do SVG é `aria-hidden` e não
 *     carrega um único número. Uma tela de login não tem sessão
 *     autenticada — não pode afirmar "112 kW agora" nem "31 ônibus no
 *     pátio", porque não tem como saber. Se cravasse, seria dado
 *     fabricado apresentado como leitura viva.
 *  2. **Tem controle de pausa.** WCAG 2.2.2: animação em loop indefinido
 *     ao lado de um formulário precisa poder parar. O botão fica fora do
 *     bloco `aria-hidden`, senão seria alcançável por Tab e invisível ao
 *     leitor de tela.
 *  3. **Não disputa a atenção com o formulário.** Só anima na primeira
 *     visita (chave versionada em localStorage), não renderiza abaixo do
 *     breakpoint do painel, e nunca toca no foco — o `autoFocus` do campo
 *     de e-mail continua sendo do formulário.
 */

/** Chave versionada: se a abertura mudar, o `_v2` volta a animar para
 *  todo mundo sem precisar limpar nada na mão do usuário. */
const ABERTURA_KEY = "nx_abertura_v1";

/**
 * A cena só existe onde o painel esquerdo existe (960px, o mesmo
 * breakpoint do `.nx-login__hero` no CSS). O gate é em JS, e não só
 * `display: none`, para que no celular o SVG animado nem entre no DOM:
 * peça decorativa em loop gasta bateria e main thread de quem está
 * tentando entrar no portal pelo telefone.
 */
const PAINEL_MQ = "(min-width: 960px)";

/**
 * Preferência de movimento reduzido do sistema. O CSS já desliga todas as
 * animações da cena sob esta media query; o JS precisa saber da preferência
 * pelo mesmo motivo que já checa os 960px — para não gravar "abertura vista"
 * quando abertura não houve.
 */
const MOVIMENTO_MQ = "(prefers-reduced-motion: reduce)";


function leAberturaVista(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(ABERTURA_KEY) === "1";
  } catch {
    // Safari em modo privado lança ao tocar localStorage. Sem persistência,
    // a abertura roda de novo — irritante, não quebrado.
    return false;
  }
}

function casaMediaQuery(mq: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(mq).matches;
}

export function LoginScene() {
  // Init lazy: decidido antes do primeiro paint, senão a cena apareceria
  // parada e depois "pularia" para animada.
  const [animar] = useState<boolean>(() => !leAberturaVista());
  const [temPainel, setTemPainel] = useState<boolean>(() => casaMediaQuery(PAINEL_MQ));
  const [reduzMovimento, setReduzMovimento] = useState<boolean>(() =>
    casaMediaQuery(MOVIMENTO_MQ),
  );
  const [pausada, setPausada] = useState(false);

  // Houve movimento de fato? É a condição que governa a gravação da chave e
  // a existência do botão de pausa. `animar` sozinho responde apenas "esta
  // instalação já viu a abertura?".
  const animou = animar && temPainel && !reduzMovimento;

  // `animou` responde "a ABERTURA rodou?". Não serve mais para governar o
  // botão de pausa, porque passou a existir movimento que não é a abertura: a
  // pulsação do realce laranja do título, que roda em TODA visita (é chamariz,
  // não introdução). Numa revisita `animou` é false, a cena nasce
  // `--sem-abertura`, e sem esta distinção o botão sumiria do DOM enquanto o
  // título continuava pulsando — animação infinita sem controle, que é
  // exatamente o que a WCAG 2.2.2 proíbe.
  //
  // A condição aqui é "existe QUALQUER movimento nesta tela": o hero aparece
  // (≥960px) e o sistema não pediu menos movimento. Abaixo de 960px o hero é
  // `display: none` e este componente já retornou null.
  // Os lacos da arte (SOC, energia, faisca, varredura, LED) rodam em TODA
  // visita desde que haja painel e o sistema nao peca menos movimento - a
  // classe `--sem-abertura` governa so a entrada encenada, nao eles. Entao
  // "ha algo para pausar" volta a ser exatamente essa condicao.
  //
  // Isto tornou desnecessario o termo de `forced-colors` que existia aqui
  // (BUG-QA3F-2). Ele cobria a combinacao "revisita + alto contraste", em que
  // a arte estava congelada e o realce do titulo morto pelo CSS: zero
  // animacoes com o botao presente. Como a arte nao congela mais na revisita,
  // essa combinacao deixou de existir - em alto contraste a arte continua
  // rodando (ela anima `transform` e `opacity`, que o modo nao sobrescreve).
  const haMovimento = temPainel && !reduzMovimento;

  // Marca a visita num efeito, não no init do useState: escrever durante o
  // render faria o StrictMode (que monta duas vezes em dev) gravar a chave
  // antes de a animação existir.
  // A condição é "houve movimento", não "era para haver". Dois gates, o
  // mesmo raciocínio:
  //  - `temPainel`: abaixo de 960px a cena nem chega ao DOM (`return null`
  //    mais abaixo), então gravar a chave ali queimaria a primeira visita de
  //    quem abriu o portal no celular — e a abertura nunca mais tocaria no
  //    desktop da garagem.
  //  - `reduzMovimento`: com movimento reduzido no sistema o CSS desliga
  //    todas as animações. Gravar a chave assim queimava a primeira visita
  //    de quem nunca viu nada — e quem depois passasse a aceitar movimento
  //    não veria a abertura nunca mais.
  useEffect(() => {
    if (!animou) return;
    try {
      window.localStorage.setItem(ABERTURA_KEY, "1");
    } catch {
      /* ignora — silenciar erro de armazenamento é aceitável */
    }
  }, [animou]);

  // A preferência de movimento pode mudar com a sessão aberta (o sistema
  // operacional a expõe em tempo real). Mesmo tratamento do breakpoint.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(MOVIMENTO_MQ);
    const aoMudar = () => setReduzMovimento(mq.matches);
    aoMudar();
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);


  // Redimensionar a janela para além do breakpoint monta/desmonta a cena.
  // Escutamos `change` do MediaQueryList E `resize` da janela: em viewport
  // emulado (DevTools, preview headless) o `change` nem sempre dispara, e
  // aí a cena ficaria montada numa largura em que não deveria existir.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(PAINEL_MQ);
    const aoMudar = () => setTemPainel(mq.matches);
    aoMudar();
    mq.addEventListener("change", aoMudar);
    window.addEventListener("resize", aoMudar);
    return () => {
      mq.removeEventListener("change", aoMudar);
      window.removeEventListener("resize", aoMudar);
    };
  }, []);

  if (!temPainel) return null;

  return (
    <div
      className={
        "nx-login__scene" +
        // Revisita (ou movimento reduzido): a cena fica no estado final, sem
        // loop. Quem já viu a abertura não precisa vê-la em toda tentativa de
        // login; quem pediu menos movimento não a vê nunca. A classe deixa
        // isso explícito no DOM em vez de depender só da media query.
        (animou ? "" : " nx-login__scene--sem-abertura") +
        (pausada ? " nx-login__scene--pausada" : "")
      }
    >
      {/* Sem animação não há o que pausar: o botão só existe quando há
          movimento de verdade para interromper — inclusive com movimento
          reduzido, em que o CSS já zera tudo (antes o botão só era escondido
          por `display: none`, ficando no DOM sem função).

          O gate é `haMovimento`, não `animou`: numa revisita a cena está
          parada mas o realce do título segue pulsando, e esse loop também
          precisa de controle. O `--pausada` daqui chega ao título por
          `:has()` no CSS — o mesmo caminho que a animação de entrada já usa
          para ler o estado da cena sem dar estado novo ao card do login. */}
      {haMovimento && (
        <button
          type="button"
          className="nx-login__scene-pause"
          // BUG-3: `aria-pressed` FOI REMOVIDO de propósito. Com ele, e com o
          // rótulo alternando junto, o leitor de tela anunciava "Retomar
          // animação, botão de alternância, PRESSIONADO" — o que sugere que
          // *retomar* está engatado, ou seja, o oposto do estado real. Ou o
          // nome é fixo e o estado vai no `aria-pressed`, ou o nome alterna e
          // não se usa `aria-pressed`. O WAI-ARIA APG recomenda a segunda
          // para play/pause, e ela ainda preserva o Label in Name (2.5.3),
          // porque nome acessível e texto visível continuam idênticos.
          onClick={() => setPausada((v) => !v)}
        >
          {/* O rótulo voltou a ser fixo, e isso É a correção do BUG-4, não um
              retrocesso. Aquele bug existia porque na revisita a cena ficava
              congelada e o botão, desenhado sobre ônibus parados, afirmava
              movimento onde não havia — a única coisa animando era o realce
              do título, 360px acima. O rótulo contextual era o remendo.

              Agora a cena anima em toda visita, então sempre que este botão
              existe há a cena inteira se movendo debaixo dele. "Pausar
              animação" voltou a ser verdade, e o remendo saiu com a causa. */}
          {pausada ? "Retomar animação" : "Pausar animação"}
        </button>
      )}
      <div className="nx-login__scene-art" aria-hidden="true">
        <CenaPatio />
      </div>
    </div>
  );
}

/** O desenho. Sem texto: legenda sobre peça em movimento não se lê, e o
 *  texto do painel já ocupa a metade de cima.
 *
 *  ## Proporção (D-019, segunda rodada)
 *
 *  O viewBox era `0 88 560 142` — 3,94:1. Com o painel de largura cheia, a
 *  aritmética dessa proporção não fechava com os 40% de altura do desenho:
 *  seriam necessários 1704px de painel numa tela de 1080px de altura. A
 *  decisão foi redesenhar mais alto, e não cortar a arte.
 *
 *  Agora é `0 0 560 246` — **2,276:1**, a proporção do mockup (728×320). O
 *  `y` continua na mesma origem, então **todo o primeiro plano ficou nas
 *  coordenadas que já estavam validadas**: o que mudou foi ganhar 88px de
 *  céu em cima e 16px de piso embaixo. Redesenho que preserva o miolo tem
 *  chance muito maior de preservar junto o que o QA achou nele.
 *
 *  ## O que entrou na altura nova — e o que NÃO entrou
 *
 *  Entrou profundidade: céu, halo de horizonte, galpão ao fundo, segunda
 *  fileira de ônibus (silhuetas) e três mastros de iluminação. Cada
 *  carregador é um totem de chão inteiro, com visor, LED e mangueira. **Nenhum
 *  deles anima.** A cena já tinha 34 animações, 29 delas infinitas, num
 *  arquivo que fica aberto em máquina de portaria; crescer a arte não pode
 *  significar crescer o custo na mesma proporção. Profundidade se faz com
 *  valor e escala, não com movimento.
 *
 *  ## `preserveAspectRatio="xMidYMax slice"`
 *
 *  A caixa da cena tem altura alvo de 40vh (ver `.nx-login__scene-art`), e
 *  a arte preenche essa caixa cortando o que sobra em vez de encolher para
 *  caber. `YMax` ancora embaixo: o que se perde primeiro, em tela larga, é
 *  o céu — que é justamente a parte de sobra. O pátio, que é o assunto,
 *  nunca sai.
 */
function CenaPatio() {
  return (
    <svg
      viewBox="0 0 560 246"
      preserveAspectRatio="xMidYMax slice"
      role="presentation"
      focusable="false"
    >
      <defs>
        <linearGradient id="nx-scene-sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" className="nx-scene__sweep-a" />
          <stop offset="50%" className="nx-scene__sweep-b" />
          <stop offset="100%" className="nx-scene__sweep-a" />
        </linearGradient>
        <linearGradient id="nx-scene-ceu" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="nx-scene__ceu-a" />
          <stop offset="100%" className="nx-scene__ceu-b" />
        </linearGradient>
        <radialGradient id="nx-scene-halo" cx="50%" cy="100%" r="70%">
          <stop offset="0%" className="nx-scene__halo-a" />
          <stop offset="100%" className="nx-scene__halo-b" />
        </radialGradient>
      </defs>

      {/* ---------- Fundo: tudo estático, de propósito ---------- */}
      <rect className="nx-scene__ceu" x="0" y="0" width="560" height="186" />
      <rect className="nx-scene__halo" x="0" y="60" width="560" height="126" />

      {/* Galpão da garagem, ao fundo. Mais escuro que o primeiro plano:
          profundidade atmosférica — o que está longe perde contraste.

          A base desce até y=140, atrás das carrocerias: galpão que "nasce"
          acima da linha do piso pareceria flutuar. O que se vê dele é o
          telhado e o que sobra nos vãos entre os ônibus.

          **Nenhum objeto do cenário acima da linha y=76.** Com `slice` ancorado embaixo, é o
          TOPO que sai de quadro em tela larga. Medido em Chrome: 20u em 1440×900,
          68u em 1920×1080, 125u em 2560×1080 e 145u em 3440×1440. O cenário
          foi posicionado abaixo de y=76 (a luminária esquerda, que é o
          elemento mais alto) para sobreviver ao caso de 1920, onde sobram 8
          unidades de folga; acima dela fica só céu, que é descartável por
          construção.

          **Em ultrawide o corte passa do céu.** A partir de ~3,29:1 a
          luminária cai; em 2560×1080 saem os mastros, os braços e as barras
          de SOC. Não é estado impossível — barra ausente não afirma nada, e
          os cabos com as faíscas sobrevivem, então a conexão continua
          desenhada. Mas a arte perde o assunto, e ultrawide em portaria é
          contexto de implantação, não extremo. Tratado em P-9. */}
      <g className="nx-scene__fundo">
        <path className="nx-scene__galpao" d="M36 140 L36 100 L168 82 L300 100 L300 140 Z" />
        <rect className="nx-scene__galpao-viga" x="32" y="96" width="272" height="6" rx="2" />
        <rect className="nx-scene__galpao-porta" x="70" y="108" width="52" height="32" rx="2" />
        <rect className="nx-scene__galpao-porta" x="142" y="108" width="52" height="32" rx="2" />
        <rect className="nx-scene__galpao-porta" x="214" y="108" width="52" height="32" rx="2" />

        <rect className="nx-scene__galpao" x="340" y="102" width="200" height="38" rx="2" />
        <rect className="nx-scene__galpao-viga" x="336" y="98" width="208" height="6" rx="2" />
        <rect className="nx-scene__galpao-porta" x="358" y="112" width="44" height="28" rx="2" />
        <rect className="nx-scene__galpao-porta" x="414" y="112" width="44" height="28" rx="2" />
        <rect className="nx-scene__galpao-porta" x="470" y="112" width="44" height="28" rx="2" />
      </g>

      {/* Mastros de iluminação do pátio. O cone é o que justifica as poças
          de luz no piso, que antes vinham de lugar nenhum.

          **Invariante:** para cada poça, o cone correspondente cobre no piso
          (y=186) o intervalo inteiro `cx ± rx`. Conferido por cálculo, não no
          olho — foi assim que QA3 achou as duas falhas: a poça do meio sem
          cone nenhum (BUG-QA3-8) e 7 unidades da poça 1 descobertas, porque
          a base do cone estava em 33 e `96 − 70 = 26` (BUG-QA3-10).

          Não há segunda fileira de ônibus ao fundo, embora ela estivesse na
          lista de sugestões: a fileira da frente ocupa a largura inteira, e
          um ônibus atrás só apareceria como lasca de 40px nos dois vãos —
          isso lê como ruído, não como profundidade. Quem faz o fundo aqui é
          o galpão. */}
      <g>
        <rect className="nx-scene__mastro" x="98" y="84" width="5" height="102" rx="2" />
        <rect className="nx-scene__luminaria" x="86" y="76" width="29" height="8" rx="3" />
        <path className="nx-scene__cone" d="M88 84 L113 84 L166 186 L26 186 Z" />
        {/* BUG-QA3-8: o mastro do meio. Sem ele a poça de cx=286 (rx=70, ou
            seja 216..356) era a única das três sem cone em cima — e ficava
            MAIS evidente depois do redesenho, porque as vizinhas ganharam
            fonte. O cone abaixo fecha exatamente 216..356 no piso. */}
        <rect className="nx-scene__mastro" x="284" y="86" width="5" height="100" rx="2" />
        <rect className="nx-scene__luminaria" x="272" y="78" width="29" height="8" rx="3" />
        <path className="nx-scene__cone" d="M274 86 L299 86 L356 186 L216 186 Z" />
        <rect className="nx-scene__mastro" x="466" y="88" width="5" height="98" rx="2" />
        <rect className="nx-scene__luminaria" x="454" y="80" width="29" height="8" rx="3" />
        <path className="nx-scene__cone" d="M456 88 L481 88 L532 186 L405 186 Z" />
      </g>

      {/* ---------- Piso ---------- */}
      <rect className="nx-scene__solo" x="0" y="186" width="560" height="60" />
      <line className="nx-scene__faixa" x1="0" y1="186" x2="560" y2="186" />
      <line className="nx-scene__faixa" x1="24" y1="200" x2="64" y2="200" />
      <line className="nx-scene__faixa" x1="204" y1="200" x2="244" y2="200" />
      <line className="nx-scene__faixa" x1="384" y1="200" x2="424" y2="200" />
      {/* Faixas mais largas e mais claras à frente: perspectiva sem truque. */}
      <line className="nx-scene__faixa nx-scene__faixa--perto" x1="0" y1="222" x2="72" y2="222" />
      <line className="nx-scene__faixa nx-scene__faixa--perto" x1="140" y1="222" x2="280" y2="222" />
      <line className="nx-scene__faixa nx-scene__faixa--perto" x1="348" y1="222" x2="488" y2="222" />

      {/* Poças de luz laranja no piso */}
      <ellipse className="nx-scene__poca" cx="96" cy="190" rx="70" ry="10" />
      <ellipse className="nx-scene__poca nx-scene__poca--d2" cx="286" cy="190" rx="70" ry="10" />
      <ellipse className="nx-scene__poca nx-scene__poca--d3" cx="470" cy="190" rx="62" ry="9" />

      {/* ---------- Carregadores ---------- */}
      {/* Carregador 1: DOIS cabos, um para cada lado. É o que resolve o
          BUG-SANITY sem inventar espaço — carregador DC de garagem com dois
          conectores é o equipamento comum, e o ônibus da esquerda deixa de
          ser o único sem cabo. Antes, um terceiro poste cairia em x ≈ −10..10
          e o cabo cruzaria a carroceria; o segundo braço não tem esse
          problema porque sai do poste que já está no lugar certo. */}
      <g>
        {/* O carregador INTEIRO, nao mais poste + caixa: um totem de recarga
            de chao, que e o que existe numa garagem de onibus. Corpo de 28x86
            indo do topo (y=100) ate o piso (y=186), com visor e LED de
            status.

            Topo em y=118, piso em y=186: 68 de altura, mais baixo que a
            versao anterior (86) a pedido do usuario. A altura de um totem de
            recarga nao precisa passar do teto do onibus - e, de fato, nao
            passa: o teto esta em y=130 e o topo do totem em 118.

            Isso obrigou a mudar a ORDEM de desenho das mangueiras (ver o
            bloco delas, abaixo dos onibus). Enquanto o totem era alto, a
            mangueira saia acima da linha do teto e ficava visivel antes de
            mergulhar atras da carroceria. Com o totem baixo isso acaba: ela
            nasceria e sumiria quase no mesmo ponto.

            Cabe no vao entre as carrocerias: onibus 1 termina em x=170,
            onibus 2 comeca em x=210. */}
        <rect className="nx-scene__carregador" x="172" y="118" width="28" height="68" rx="5" />
        <rect className="nx-scene__carregador-tela" x="177" y="125" width="18" height="13" rx="2" />
        {/* Duas linhas de leitura no visor: e o detalhe que faz a caixa ler
            como APARELHO e nao como retangulo escuro. Larguras
            diferentes imitam um valor e uma unidade. */}
        <rect className="nx-scene__carregador-linha" x="180" y="129" width="12" height="2" rx="1" />
        <rect className="nx-scene__carregador-linha nx-scene__carregador-linha--curta" x="180" y="133.5" width="7" height="2" rx="1" />
        <circle className="nx-scene__led" cx="186" cy="146" r="3" />
      </g>
      {/* Carregador 2 */}
      <g>
        {/* Mesmo totem, no vao entre o onibus 2 (termina em 360) e o onibus 3
            (comeca em 400). */}
        <rect className="nx-scene__carregador" x="364" y="118" width="28" height="68" rx="5" />
        <rect className="nx-scene__carregador-tela" x="369" y="125" width="18" height="13" rx="2" />
        {/* Duas linhas de leitura no visor: e o detalhe que faz a caixa ler
            como APARELHO e nao como retangulo escuro. Larguras
            diferentes imitam um valor e uma unidade. */}
        <rect className="nx-scene__carregador-linha" x="372" y="129" width="12" height="2" rx="1" />
        <rect className="nx-scene__carregador-linha nx-scene__carregador-linha--curta" x="372" y="133.5" width="7" height="2" rx="1" />
        <circle className="nx-scene__led nx-scene__led--b" cx="378" cy="146" r="3" />
      </g>

      {/* Ônibus 1 — agora cabeado pelo braço esquerdo do carregador 1, e por
          isso a barra de SOC VOLTA a encher. O que não pode existir é barra
          subindo sem cabo, sem poste e sem faísca: era esse o estado
          fisicamente impossível que o QA3 achou, e a correção de então (SOC
          parado em 100%) era o melhor possível com 142px de altura. Com o
          segundo braço, o ônibus tem de fato de onde receber energia. */}
      <g>
        <rect className="nx-scene__corpo" x="20" y="130" width="150" height="50" rx="8" />
        <rect className="nx-scene__teto" x="26" y="120" width="138" height="12" rx="5" />
        <rect className="nx-scene__soc-trilho" x="34" y="123" width="118" height="6" rx="3" />
        <rect
          className="nx-scene__soc-fill nx-scene__soc-fill--a"
          x="34"
          y="123"
          width="118"
          height="6"
          rx="3"
        />
        <rect className="nx-scene__janela" x="30" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d2" x="60" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d3" x="90" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d4" x="120" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d5" x="148" y="139" width="14" height="18" rx="3" />
        <circle className="nx-scene__roda" cx="52" cy="182" r="9" />
        <circle className="nx-scene__roda" cx="138" cy="182" r="9" />
      </g>

      {/* Ônibus 2 — recebendo carga */}
      <g>
        <rect className="nx-scene__corpo" x="210" y="130" width="150" height="50" rx="8" />
        <rect className="nx-scene__teto" x="216" y="120" width="138" height="12" rx="5" />
        <rect className="nx-scene__soc-trilho" x="224" y="123" width="118" height="6" rx="3" />
        <rect
          className="nx-scene__soc-fill nx-scene__soc-fill--b"
          x="224"
          y="123"
          width="118"
          height="6"
          rx="3"
        />
        <rect className="nx-scene__janela" x="220" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d2" x="250" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d3" x="280" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d4" x="310" y="139" width="24" height="18" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d5" x="338" y="139" width="14" height="18" rx="3" />
        <circle className="nx-scene__roda" cx="242" cy="182" r="9" />
        <circle className="nx-scene__roda" cx="328" cy="182" r="9" />
      </g>

      {/* Ônibus 3 — parcialmente fora do quadro, dá profundidade ao pátio */}
      <g>
        <rect className="nx-scene__corpo" x="400" y="134" width="150" height="46" rx="8" />
        <rect className="nx-scene__teto" x="406" y="125" width="138" height="11" rx="5" />
        <rect className="nx-scene__soc-trilho" x="414" y="127.5" width="118" height="6" rx="3" />
        <rect
          className="nx-scene__soc-fill nx-scene__soc-fill--c"
          x="414"
          y="127.5"
          width="118"
          height="6"
          rx="3"
        />
        <rect className="nx-scene__janela" x="410" y="142" width="22" height="17" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d2" x="438" y="142" width="22" height="17" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d3" x="466" y="142" width="22" height="17" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d4" x="494" y="142" width="22" height="17" rx="3" />
        <rect className="nx-scene__janela nx-scene__janela--d5" x="522" y="142" width="18" height="17" rx="3" />
        <circle className="nx-scene__roda" cx="430" cy="182" r="8.5" />
        <circle className="nx-scene__roda" cx="516" cy="182" r="8.5" />
      </g>

      {/* Cabos conectados às tomadas na lateral baixa, abaixo das janelas. */}
      <path className="nx-scene__cabo" d="M172 145 C171 151 170 159 167 166" />
      <path className="nx-scene__energia nx-scene__energia--c" d="M172 145 C171 151 170 159 167 166" />
      <path className="nx-scene__cabo" d="M200 145 C204 151 208 159 212 166" />
      <path className="nx-scene__energia" d="M200 145 C204 151 208 159 212 166" />
      <path className="nx-scene__cabo" d="M392 145 C397 151 400 159 404 166" />
      <path className="nx-scene__energia nx-scene__energia--b" d="M392 145 C397 151 400 159 404 166" />
      <circle className="nx-scene__porta" cx="167" cy="166" r="4" />
      <circle className="nx-scene__porta" cx="212" cy="166" r="4" />
      <circle className="nx-scene__porta" cx="404" cy="166" r="4" />
      {/* Faíscas nos pontos de conexão — uma por cabo. */}
      <circle className="nx-scene__faisca nx-scene__faisca--c" cx="167" cy="166" r="3.4" />
      <circle className="nx-scene__faisca" cx="212" cy="166" r="3.4" />
      <circle className="nx-scene__faisca nx-scene__faisca--b" cx="404" cy="166" r="3.4" />

      {/* Varredura de luz atravessando o pátio. Cobre a altura nova inteira:
          parar no antigo y=96 deixaria o céu fora do efeito e a emenda
          apareceria. */}
      <rect className="nx-scene__varredura" x="0" y="0" width="140" height="246" />
    </svg>
  );
}
